import { z } from "zod";
import { FieldLevel, FieldType } from "../../dtos/property-stage";
import {
  FormSection,
  HospitalityTypes,
  PropertyAmenity,
  Track,
} from "../../types";

interface GenerateZodSchemaProps {
  sections: FormSection[];
  amenitiesRequired: boolean;
  docsRequired: boolean;
}

interface GenerateDefaultValuesProps {
  sections?: FormSection[];
  track: Track;
  amenitiesRequired: boolean;
  docsRequired: boolean;
}

function isDynamicField(field: {
  name: string;
  fieldType: FieldType | null;
  dynamicFormUrl?: string | null;
}): boolean {
  if (field.fieldType === FieldType.Dynamic) {
    return true;
  }
  const n = field.name.toLowerCase();
  return n.startsWith("dynamic") && !!field.dynamicFormUrl;
}

function defaultValueForField(field: FormSection["fields"][number]): unknown {
  if (field.isDropDown) {
    return [];
  }
  if (isDynamicField(field)) {
    return null;
  }
  switch (field.fieldType) {
    case FieldType.Boolean:
      return false;
    case FieldType.Number:
      return "";
    case FieldType.Date:
    case FieldType.DateTime:
      return undefined;
    case FieldType.Time:
      return "";
    default:
      return "";
  }
}

export const generateZodSchema = ({
  sections,
  amenitiesRequired,
  docsRequired,
}: GenerateZodSchemaProps) => {
  const shape: Record<string, z.ZodTypeAny> = {};

  sections.forEach((section) => {
    const sectionShape: Record<string, z.ZodTypeAny> = {};
    const hasRentalTag = section.fields.some(
      (field) => field.name === "rentalTag",
    );

    section.fields.forEach((field) => {
      let validator: z.ZodTypeAny = z.string();

      if (isDynamicField(field) || field.isDropDown) {
        validator = z.object(
          {
            label: z.string(),
            value: z.string(),
          },
          {
            required_error: `${field.name} is required`,
          }
        );
      } else {
        switch (field.fieldType) {
          case FieldType.String:
            validator = z.string({
              required_error: `${field.name} is required`,
            });
            break;
          case FieldType.Number: {
            let numberValidator = z.coerce
              .number({ invalid_type_error: `${field.name} must be a number` })
              .int({ message: `${field.name} must be an integer` });

            if (field.minValue !== null && field.minValue !== undefined) {
              numberValidator = numberValidator.min(field.minValue, {
                message: `${field.name} must be at least ${field.minValue}`,
              });
            }
            if (field.maxValue !== null && field.maxValue !== undefined) {
              numberValidator = numberValidator.max(field.maxValue, {
                message: `${field.name} must be at most ${field.maxValue}`,
              });
            }

            validator = numberValidator;
            break;
          }
          case FieldType.Boolean:
            validator = z.boolean({
              invalid_type_error: `${field.name} must be true/false`,
            });
            break;
          case FieldType.Date: {
            let dateValidator = z.coerce.date({
              invalid_type_error: `${field.name} must be a valid date`,
            });

            if (field.minDate) {
              dateValidator = dateValidator.min(new Date(field.minDate), {
                message: `${field.name} must be on or after ${new Date(
                  field.minDate
                ).toLocaleDateString()}`,
              });
            }
            if (field.maxDate) {
              dateValidator = dateValidator.max(new Date(field.maxDate), {
                message: `${field.name} must be on or before ${new Date(
                  field.maxDate
                ).toLocaleDateString()}`,
              });
            }

            validator = dateValidator;
            break;
          }
          case FieldType.DateTime: {
            let dateTimeValidator = z.coerce.date({
              invalid_type_error: `${field.name} must be a valid date/time`,
            });

            if (field.minDateTime) {
              dateTimeValidator = dateTimeValidator.min(
                new Date(field.minDateTime),
                {
                  message: `${field.name} must be on or after ${new Date(
                    field.minDateTime
                  ).toLocaleString()}`,
                }
              );
            }
            if (field.maxDateTime) {
              dateTimeValidator = dateTimeValidator.max(
                new Date(field.maxDateTime),
                {
                  message: `${field.name} must be on or before ${new Date(
                    field.maxDateTime
                  ).toLocaleString()}`,
                }
              );
            }

            validator = dateTimeValidator;
            break;
          }
          case FieldType.Time:
            validator = z
              .string()
              .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, {
                message: `${field.name} must be a valid time`,
              });
            break;
        }
      }

      // For required fields, allow null values
      sectionShape[field.name] = field.isRequired
        ? validator
        : z.union([validator, z.null()]);
    });

    // hospitalityType is injected client-side as a dependent of rentalTag.
    // It is optional in the shape but required (via refine below) when
    // rentalTag is set to hospitality.
    if (hasRentalTag) {
      sectionShape["hospitalityType"] = z
        .object({ label: z.string(), value: z.string() })
        .nullish();
    }

    let sectionSchema: z.ZodTypeAny = z.object(sectionShape);

    if (hasRentalTag) {
      sectionSchema = sectionSchema.superRefine((data, ctx) => {
        const rentalTag = (data as Record<string, unknown>).rentalTag;
        const rentalTagValue =
          typeof rentalTag === "string"
            ? rentalTag
            : (rentalTag as { value?: string } | undefined)?.value;
        if (
          rentalTagValue === "hospitality" &&
          !(data as Record<string, unknown>).hospitalityType
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Hospitality type is required when rental type is Hospitality",
            path: ["hospitalityType"],
          });
        }
      });
    }

    shape[section.fieldLevel] = sectionSchema;
  });

  if (amenitiesRequired) {
    shape["amenities"] = z.instanceof(Set, {
      message: "Amenities must be a Set",
    });
  }
  if (docsRequired) {
    shape["documents"] = z.array(z.any()).optional();
  }

  return z.object(shape);
};

export const generateDefaultValues = ({
  sections,
  track,
  amenitiesRequired,
}: GenerateDefaultValuesProps) => {
  const defaultValues: Record<string, any> = {};

  sections?.forEach((section) => {
    switch (section.fieldLevel) {
      case FieldLevel.Property:
        defaultValues[section.fieldLevel] = track?.property
          ? {
              ...track?.property,
              finishing: {
                label: track?.property?.finishing,
                value: track?.property?.finishing,
              },
              propertyTypeId: {
                label: track?.property?.propertyType?.name,
                value: track?.property?.propertyType?.id?.toString() || "",
              },
              compoundId: {
                label: track?.property?.compound?.name,
                value: track?.property?.compound?.id.toString(),
              },
            }
          : {};
        break;
      case FieldLevel.PropertyPrice:
        defaultValues[section.fieldLevel] =
          track?.property?.propertyPrice || {};
        break;
      case FieldLevel.propertyRental:
        defaultValues[section.fieldLevel] =
          track?.property?.propertyRental || {};
        break;
      case FieldLevel.PropertyHospitality:
        defaultValues[section.fieldLevel] = {};
        break;
      case FieldLevel.PropertyFinishing:
        defaultValues[section.fieldLevel] = {
          ...(track?.property?.propertyFinishing || {}),
          // hospitalityType lives on the Property (not propertyFinishing); seed
          // it as an Option so the injected dropdown shows the saved value.
          ...(track?.property?.hospitalityType
            ? {
                hospitalityType: {
                  label: HospitalityTypes[track.property.hospitalityType],
                  value: track.property.hospitalityType,
                },
              }
            : {}),
        };
        break;
      case FieldLevel.Track:
        defaultValues[section.fieldLevel] = track || {};
        break;
      case FieldLevel.Custom: {
        const initial: Record<string, unknown> = {};
        section.fields.forEach((f) => {
          initial[f.name] = defaultValueForField(f);
        });
        defaultValues[section.fieldLevel] = initial;
        break;
      }
      default:
        break;
    }

    if (amenitiesRequired) {
      defaultValues["amenities"] = new Set(
        track?.property?.propertyAmenity?.map(
          (amenity: PropertyAmenity) => amenity.amenity.id
        )
      );
    }
  });

  return defaultValues;
};
